import { useCallback, useMemo, useState } from "react"
import PropTypes from "prop-types"
import classNames from "classnames"
import { some, includes, filter } from "lodash"
import { Button, Popover, Box } from "@mui/material"
import { FILTER_ON_GROUP } from "helpers/shapes"
import { SECURITY_IDS } from "./shapes"
import SearchableSelector from "./searchable-selector"
import SecuritiesSearch from "./securities-search"
import { compareSecurity, convertUnifiedSearchItemToFilterItem, mapFilterOns, EMPTY_FILTER, makeFormattedOptions, getValidFilters } from "./helpers"
import FilterSelectors from "./filter-selectors"
import "./style.scss"

const DEFAULT_FILTER_NOTE = "Filters apply to both portfolio and selected benchmark / comparison."

export default function AddFilter({
  filterOns,
  currency,
  heapAddFilterId,
  updateFilters,
  account,
  modelId,
  assetId,
  assetType,
  startDate,
  endDate,
  intraday,
  isExpanded,
  securityIds,
  anchorEl,
  filtersVisible,
  toggleFilters,
  existingFilters,
  additionalFilterComponent,
  showAdditionalFilterComponentOnActiveFilterOnly = false,
  forceShowAdditionalFilterComponent = false,
  isApplyButtonDisabledFunction,
  filterNote = DEFAULT_FILTER_NOTE,
}) {
  const [anchorElement, setAnchorElement] = useState(null)
  const [activeSearchIndex, setActiveSearchIndex] = useState(null)
  const [filters, setFilters] = useState(() => existingFilters && existingFilters.length > 0 ? [...existingFilters] : EMPTY_FILTER)
  const showFilterOnOptionsOnly = filters.length === 1 && !filters[0].filterOn

  const handleCloseSecuritiesSearch = useCallback(() => {
    setAnchorElement(null)
    setActiveSearchIndex(null)
  }, [])

  const handleSetFilterOn = (index, filterOn) => {
    setFilters(currentFilters => mapFilterOns(currentFilters, index, filterOn))
  }

  const handleAppendFilterValue = useCallback((index, item) => {
    setFilters(currentFilters => currentFilters.map((filter, i) =>
      i === index
        ? { ...filter, values: [...filter.values, item] }
        : filter,
    ))
  }, [])

  const deselectSecurity = useCallback((item) => {
    const { assetClass, exchange, ticker } = item
    setFilters(currentFilters => currentFilters.map((filterObj, i) => i === activeSearchIndex
      ? { ...filterObj, values: filter(filterObj.values, v => {
        if (assetClass === "index") {
          return v.indexId !== ticker
        } else {
          return !(v.ticker === ticker && v.mic === exchange)
        }
      }),
      } : filterObj,
    ))
  }, [activeSearchIndex])

  const deselectIndex = useCallback((id) => {
    setFilters(currentFilters => currentFilters.map((filterObj, i) =>
      i === activeSearchIndex
        ? { ...filterObj, values: filter(filterObj.values, v => v.indexId !== id) }
        : filterObj,
    ))
  }, [activeSearchIndex])

  const disableSearchResult = useCallback(() => (searchResult) => {
    if (!includes(["security", "index"], searchResult.type)) {
      return true
    }
    return false
  }, [])

  const handleSecuritySelected = useCallback((index) => (searchResult) => {
    const currentValues = filters[index]?.values || []
    return some(currentValues, selectedSecurity => {
      if (searchResult.type === "index") {
        return selectedSecurity.indexId === searchResult.id
      } else {
        if (selectedSecurity.assetClass === "index") {
          return selectedSecurity.indexId === searchResult.ticker
        } else {
          return selectedSecurity.ticker === searchResult.ticker && selectedSecurity.mic === searchResult.mic
        }
      }
    })
  }, [filters])

  const itemsActions = useMemo(() => {
    if (activeSearchIndex === null) return {}

    return {
      index: {
        noDispatch: true,
        mainAction: item => () => {
          const { type, id, name } = item
          const currentValues = filters[activeSearchIndex]?.values || []
          const isSelected = some(currentValues, selectedItem => selectedItem.indexId === id)
          if (isSelected) {
            deselectIndex(id)
          } else {
            handleAppendFilterValue(activeSearchIndex, { id, assetClass: type, indexId: id, value: id, label: name })
          }
        },
      },
      security: {
        noDispatch: true,
        mainAction: item => () => {
          const currentValues = filters[activeSearchIndex]?.values || []
          const security = convertUnifiedSearchItemToFilterItem(item)
          const isSelected = some(currentValues, selectedSecurity => compareSecurity(selectedSecurity, security))
          if (isSelected) {
            deselectSecurity(item)
          } else {
            handleAppendFilterValue(activeSearchIndex, security)
          }
        },
      },
    }
  }, [activeSearchIndex, handleAppendFilterValue, deselectSecurity, filters, deselectIndex])

  const handleClose = () => {
    setFilters(EMPTY_FILTER)
    toggleFilters()
  }

  const formattedOptions = useMemo(() => makeFormattedOptions(filterOns), [filterOns])

  const isApplyButtonDisabled = useMemo(() => {
    const validFilters = getValidFilters(filters)
    if (isApplyButtonDisabledFunction) {
      return validFilters.length !== filters.length && isApplyButtonDisabledFunction()
    }
    return validFilters.length !== filters.length
  }, [filters, isApplyButtonDisabledFunction])

  const handleApplyAllFilters = () => {
    updateFilters(filters)
    handleClose()
  }

  if (showFilterOnOptionsOnly && filtersVisible && !forceShowAdditionalFilterComponent) {
    return (
      <SearchableSelector
        items={formattedOptions}
        current={null}
        onSelect={(item) => handleSetFilterOn(0, item)}
        onClickAway={handleClose}
        placeholder="Search..."
        isOpen={filtersVisible}
        anchorElement={anchorEl}
        header="Select a Filter"
        className="filter-on-dropdown"
      />
    )
  }

  return (
    <>
      <Popover
        open={filtersVisible}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 0,
              width: 680,
              maxHeight: "600px",
            },
          },
        }}
      >
        <div className="components_add-filter-new">
          <FilterSelectors
            filters={filters}
            formattedOptions={formattedOptions}
            setFilters={setFilters}
            currency={currency}
            handleClose={handleClose}
            additionalFilterComponent={additionalFilterComponent}
            forceShowAdditionalFilterComponent={forceShowAdditionalFilterComponent}
            showAdditionalFilterComponentOnActiveFilterOnly={showAdditionalFilterComponentOnActiveFilterOnly}
            setAnchorElement={setAnchorElement}
            setActiveSearchIndex={setActiveSearchIndex}
            updateFilters={updateFilters}
            handleSetFilterOn={handleSetFilterOn}
          />
          <div className="filter-actions">
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1 }}>
              <Button className="cancel-btn" onClick={handleClose} variant="contained">Cancel</Button>
              <Button
                className="apply-btn"
                disabled={isApplyButtonDisabled}
                onClick={handleApplyAllFilters}
                variant="contained"
                color="primary"
                id={heapAddFilterId}
              >
                Apply
              </Button>
            </Box>
          </div>
          <div className="filter-info-text">
            <span>{filterNote}</span>
          </div>
        </div>
      </Popover>
      <Popover
        className="securities-search-popover"
        onClose={handleCloseSecuritiesSearch}
        open={Boolean(anchorElement)}
        anchorEl={anchorElement}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 0,
              minHeight: 0,
            },
          },
        }}
      >
        <div className={classNames("securities-search-wrapper", { hidden: !anchorElement })}>
          <SecuritiesSearch
            account={account}
            modelId={modelId}
            assetId={assetId}
            assetType={assetType}
            itemsActions={itemsActions}
            disableSearchResult={disableSearchResult(activeSearchIndex)}
            securitySelected={handleSecuritySelected(activeSearchIndex)}
            startDate={startDate}
            endDate={endDate}
            intraday={intraday}
            isExpanded={isExpanded}
            securityIds={securityIds}
          />
        </div>
      </Popover>
    </>
  )
}

AddFilter.propTypes = {
  filterOns: PropTypes.arrayOf(FILTER_ON_GROUP).isRequired,
  addFilter: PropTypes.func.isRequired,
  heapAddFilterId: PropTypes.string.isRequired,
  account: PropTypes.string,
  modelId: PropTypes.string,
  assetId: PropTypes.string,
  assetType: PropTypes.string,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  intraday: PropTypes.bool,
  isExpanded: PropTypes.bool,
  currency: PropTypes.string,
  securityIds: SECURITY_IDS,
  anchorEl: PropTypes.object,
  filtersVisible: PropTypes.bool.isRequired,
  toggleFilters: PropTypes.func.isRequired,
  removeFilter: PropTypes.func,
  editFilter: PropTypes.func,
  existingFilters: PropTypes.array,
  additionalFilterComponent: PropTypes.node,
  updateFilters: PropTypes.func,
  showAdditionalFilterComponentOnActiveFilterOnly: PropTypes.bool,
  forceShowAdditionalFilterComponent: PropTypes.bool,
  isApplyButtonDisabledFunction: PropTypes.func,
  filterNote: PropTypes.string,
}
